<template>
    <div class="o-map-guide-views">
        <GuideView
            v-if="isGuideView"
            :render-trigger="guideRenderTrigger"
            :transition-duration="transitionDuration"

            @floorClick="onFloorClick"
            @transitionStart="onTransitionStart"
            @transitionEnd="onTransitionEnd"

            :allow-rotation="canRotateMap"

            :highlighted-units="[endUnit]"

            :camera-tilt="guideTilt"

            :show-floors="visibleFloorsUnitNames"

            :separate-distance="floorSeparationDistance"
            :separate-levels="separateLevels"
            :separate-floors="floorsHeightOffset"

            :fade-out-floors-except="highlightedFloorsUnitNames"
            :fade-out-covers="greyedCoversBuildingsIDs"
            :camera-orientation="guideSteps[0].camera_orientation"

            :highlight-color="getGuideConfig('destination_unit_color','#E25046')"

            :max-zoom-offset="getGuideCameraConfig('max_zoom_offset',1500)"
            :max-zoom-margin="maxZoomMargin"

            :center-on-guide="true"
            :zoom-on-guide="path.world_distance > getGuideCameraConfig('short_guide_distance_threshold',50)"
            :zoom="guideMaxZoom"
            :transparent-floors="pathTransparentFloorsIDs"
            :transparent-covers="transparentCoversBuildingsIDs"

            :camera-zoom-margin="maxZoomMargin"

            :limitMinZoomTo="getGuideCameraConfig('guide_min_zoom', 400)"

            :show-covers="notInPathBuildingGroupsBuildingsIDs"
            :show-outdoor="showOutdoor"

            :constrain-to-level="outdoorLevels"
            :show-icons="pathBuildingGroupsFloorsIcons"
        >
            <div v-if="shouldShowBubbles">
                <OGuideViewBubbles
                    v-for="step in guidePortals"
                    :key="`${step.portal.id}-${step.index}`"
                    :guideStepIndex="step.index"
                    :guideSteps="guideSteps"
                    :step="step.step"
                    :vertices="vertices"
                    target="guide_step"
                    @setActiveGuideStep="setActiveGuideStep(step.index)"
                >
                    <ADialog>
                        <template v-if="getStepIconUrl(step.index)" #icon>
                            <img
                                :src="getStepIconUrl(step.index)"
                                class="dialog__icon"
                                :alt="`${$t('get-step-dialog_icon')}`"
                            />
                        </template>
                    </ADialog>
                </OGuideViewBubbles>
                <OGuideViewBubbles
                    v-if="endObject"
                    :step="endObject"
                    :guideSteps="guideSteps"
                    :guideStepIndex="endUnit"
                    :vertices="vertices"
                    target="unit"
                    @setActiveGuideStep="setActiveGuideStep(guideSteps.length -1)"
                >
                    <ADialog >
                        <template v-if="shouldShowDestinationName" >
                            <OStepHyphensText
                                class="guide-view__end-name"
                                :text="t(endObject,'name')"
                            />
                        </template>
                    </ADialog>
                </OGuideViewBubbles>
                <OGuideViewBubbles
                    v-if="startGuideStep && shouldShowYouAreHere"
                    :step="startGuideStep"
                    :guideSteps="guideSteps"
                    :guideStepIndex="0"
                    :vertices="vertices"
                    target="guide_step"
                    @click.native="setActiveGuideStep(0)"
                >
                    <ADialog class="dialog__you-here-wrapper">
                        <AYouAreHere></AYouAreHere>
                    </ADialog>
                </OGuideViewBubbles>

                <OGuideViewBubbles
                    v-else-if="startGuideStep"
                    :step="startGuideStep"
                    :guideSteps="guideSteps"
                    :guideStepIndex="0"
                    :vertices="vertices"
                    target="guide_step"
                    @setActiveGuideStep="setActiveGuideStep(0)"
                >
                    <ADialog>
                        <template>
                            <span class="guide-steps__step-number">
                                {{ 1 }}
                            </span>
                            <span class="guide-steps__step-direction">
                                <svg-icon
                                    v-if="['up','down'].includes(startGuideStep.direction)"
                                    original
                                    :name="`navigation/${startGuideStep.direction}-dark`"
                                />
                                <svg-icon
                                    v-else
                                    original
                                    :name="`navigation/${startGuideStep.direction}-black`"
                                />
                            </span>
                            <OStepText v-if="shouldShowGuideStepText" class="d-path-steps__step" :step="startGuideStep"></OStepText>
                        </template>
                    </ADialog>
                </OGuideViewBubbles>
            </div>
        </GuideView>

        <GuideStepView
            v-if="isGuideStepView"
            @transitionStart="onTransitionStart"
            @transitionEnd="onTransitionEnd"
            :transition-duration="guideStepTransitionDuration"

            :highlight-color="getGuideConfig('destination_unit_color','#E25046')"

            @floorClick="onFloorClick"

            :allow-rotation="getGuideCameraConfig('allow_orientation', false) || canOrient"

            :highlighted-units="[endObject.unit.name]"

            :show-floors="visibleFloorsUnitNames"

            :separate-distance="floorSeparationDistance"
            :separate-levels="separateLevels"
            :separate-floors="floorsHeightOffset"

            :fade-out-floors-except="activeGuideStepLevelFloorsUnitNames"

            :camera-tilt="activeGuideStepCameraTilt"

            :zoom-on-guide-steps="activeGuideSteps"
            :zoom-on-guide-step-radius="activeGuideStepRadius"
            :limit-max-zoom-to-floors="pathIndoorFloorsUnitNames"
            :limit-min-zoom-to="getGuideCameraConfig('guide_step_min_zoom', 300)"

            :center-on-guide-steps="activeGuideSteps"
            :transparent-floors="pathStepTransparentFloorsIDs"
            :transparent-covers="transparentCoversBuildingsIDs"
            :render-trigger="guideStepRenderTrigger"
            :camera-zoom-margin="getGuideCameraConfig('guide_step_view_margin', 0.0)"

            :show-covers="notInPathBuildingGroupsBuildingsIDs"
            :show-outdoor="showOutdoor"

            :constrain-to-level="outdoorLevels"
            :show-icons="pathBuildingGroupsFloorsIcons"
        >

            <div v-if="shouldShowBubbles">
                <OGuideViewBubbles
                    v-if="activeGuideStepIndex === 0 && shouldShowYouAreHere"
                    :step="startGuideStep"
                    :guideSteps="guideSteps"
                    :guideStepIndex="0"
                    :vertices="vertices"
                    target="guide_step"
                    @click.native="setActiveGuideStep(0)"
                >
                    <ADialog class="dialog__you-here-wrapper">
                        <AYouAreHere></AYouAreHere>
                    </ADialog>
                </OGuideViewBubbles>
                <OGuideStepBubble
                    v-else-if="shouldShowActiveGuideStepBubble"
                    :guideSteps="guideSteps"
                    :activeGuideStepIndex="activeGuideStepIndex"
                    :shouldShowGuideStepText="shouldShowGuideStepText"
                    :vertices="vertices"
                />
                <OGuideViewBubbles
                    v-if="shouldShowFinishStepBubble"
                    :step="endObject"
                    :guideSteps="guideSteps"
                    :guideStepIndex="endUnit"
                    :vertices="vertices"
                    target="unit"
                    @setActiveGuideStep="setActiveGuideStep(guideSteps.length -1)"
                >
                    <ADialog icon="marker">
                        <template v-if="shouldShowDestinationName" >
                            <OStepHyphensText
                                class="guide-view__end-name"
                                :text="t(endObject,'name')"
                            />
                        </template>
                    </ADialog>
                </OGuideViewBubbles>
            </div>
        </GuideStepView>
        <div class="o-map-guide-views_overlay"></div>
    </div>
</template>

<script>
import {
    mapState,
    mapGetters
} from 'vuex';

import ADialog from "../../atoms/a-dialog/a-dialog";
import AYouAreHere from "../../atoms/a-you-are-here/a-you-are-here";
import OStepText from "../../unique-organisms/o-step-text/o-step-text";
import OGuideStepBubble from '../o-guide-step-bubble/o-guide-step-bubble';
import OStepHyphensText from "../o-step-hyphens-text/o-step-hyphens-text";
import OGuideViewBubbles from '../o-guide-view-bubbles/o-guide-view-bubbles';
import GuideView from "../../../shared/components/map-viewer/views/guide/guide-view";
import GuideStepView from "../../../shared/components/map-viewer/views/guide-step/guide-step-view";

import {
    UI_STEP_SELECTED,
    MAP_SHOW_PATH_STEPS,
    UI_STEP_BY_STEP_DIRECTIONS
} from "@/shared/components/map-viewer/constants/map-events";

import {
    REMOVE_PATH,
    RESET_MAP_VIEW,
    // REBUILD_PATH,
    // MAP_SHOW_PATH,
    SET_GYROSCOPE_STATE,
    // SET_NORMALIZED_ZOOM,
    // START_GUIDE_OVERVIEW
} from "@/shared/components/map-viewer/constants/map-commands";

import {
    set2ObjectProp,
    pushByComplexIndex,
    pushUnique2ObjectProp,
} from "@/utils/helpers/map-views-helper";
import * as Sentry from "@sentry/browser";
//import Severity from "@sentry/types";

export default {
    name: "OMapGuideViews",
    components: {
        ADialog,
        OStepText,
        GuideView,
        AYouAreHere,
        GuideStepView,
        OGuideStepBubble,
        OStepHyphensText,
        OGuideViewBubbles
    },

    // -------------------------------- Injected ----------------------------------------
    inject: ['mapViewer'],

    // -------------------------------- Data --------------------------------------------
    data(){
        return {
            guideStepRenderTrigger: 0,
            guideRenderTrigger: 0,
            canOrient: false,
            iconsMap:{
                forward: 'straight',
                left: 'left',
                right: 'right',
                up: 'elevator',
                down: 'elevator'
            },
            cantRiseFloors: [],
            transitionDuration: 0,
            guideStepTransitionDuration: 0,
            maxZoomMargin: 0.7,
            isFirstView: true,
            isViewInTransition: true,
        }
    },

    // -------------------------------- Computed ----------------------------------------
    computed: {
        ...mapState({
            path: state => state.activePath,
            isCompassEnabled: state => state.isCompassEnabled,
            activeGuideStepIndex: state => state.activeStepIndex,
            distance: state => state.activePath?.distance,
            map: state => state.map,
        }),
        ...mapGetters([
            't',
            'isTerminal',
            'getMapConfig',
            'getMapConfigProp',
        ]),

        canRotateMap(){
            //todo: re-think logic
            return (this.isCompassEnabled && !this.isMobile) ||
                this.getGuideCameraConfig('allow_orientation', false) ||
                this.canOrient ||
                this.isDebug
        },
        objects(){
            return this.mapViewer.mapData.objects;
        },
        portals(){
            return this.mapViewer.mapData.objects.filter( o => o.type === 'portal');
        },
        entrances(){
            return this.portals.filter( p => p.meta.is_entrance )
        },
        shouldShowYouAreHere(){
            return this.getGuideCameraConfig('steps.show_you_are_here', false) && this.isStartPositionConfirmed
        },
        shouldShowGuideStepNumber(){
            return this.getGuideCameraConfig('steps.active.show_step_numbers', true)
        },
        shouldShowGuideStepText(){
            return this.getGuideCameraConfig('steps.active.show_text', false)
        },
        shouldShowActiveGuideStepBubble(){
            return this.getGuideCameraConfig('steps.active.show_bubble', false)
        },
        shouldShowGuideStepDirectionIcon(){
            return this.getGuideCameraConfig('steps.active.show_direction_icon', true)
        },
        shouldShowDestinationName(){
            return this.getGuideCameraConfig('steps.last.show_destination_name', true)
        },
        isStartPositionConfirmed(){
            return this.isTerminal || this.isGpsPosition || this.isQrPosition
        },
        isGpsPosition(){ return false },
        isQrPosition(){ return false },

        guideTilt(){
            if(this.isUndergroundGuide)
                return this.getGuideCameraConfig('tilt_for_underground_guide',18)
            if(this.pathLevels.length > 2 && this.isShortGuide)
                return this.getGuideCameraConfig('tilt_for_3plus_level_guide',18) + 10
            if(this.pathLevels.length > 1 && this.isShortGuide)
                return this.getGuideCameraConfig('tilt_for_2_level_guide',22) + 10
            if(this.pathLevels.length > 2)
                return this.getGuideCameraConfig('tilt_for_3plus_level_guide',18)
            if(this.pathLevels.length > 1)
                return this.getGuideCameraConfig('tilt_for_2_level_guide',22)
            if(this.path.world_distance < this.getGuideCameraConfig('short_guide_distance_threshold',50))
                return this.getGuideCameraConfig('tilt_for_short_guide',45)
            return this.getGuideCameraConfig('tilt_default',32)
        },
        isShortGuide(){
            return this.path.world_distance < this.getGuideCameraConfig('short_guide_distance2zoom',50)
        },
        guideMaxZoom(){
            const c = this.getGuideCameraConfig('short_guide_distance2zoom',50)
            const gd = this.path.world_distance
            const min = this.getGuideCameraConfig('guide_min_zoom',400)
            const separation = this.getGuideCameraConfig('separate_floor_distance',40)

            let zoomDist = gd * c < min ? min : gd* c

            // On outdoor guides increase zoom distance
            if(this.pathFloors.find( f => f.meta.is_outdoor )){
                zoomDist = zoomDist * this.getGuideCameraConfig('short_outdoor_guide_zoom_multiplier', 3)
            }

            // Adjust zoom when multilevel guide
            if( this.pathLevels.length )
                zoomDist = zoomDist + this.pathLevels.length * separation * 6

            if(this.isFirstView)
                zoomDist*=3;

            return zoomDist
        },
        guideSteps(){
            return this.path && this.path.steps || []
        },

        vertices(){
            return this.path && this.path.vertices || []
        },
        guideVerticesFloorsIDs(){
            let prevFloorId = null;
            return this.vertices.reduce( (acc, v, index) => {
                if(prevFloorId !== v.floor)
                {
                    acc.push(v.floor)
                    prevFloorId = v.floor
                }
                return acc
            },[])
        },
        guideVerticesFloors(){
            return this.guideVerticesFloorsIDs.map( fid => this.floors.find( f => f.id === fid ))
        },
        guideVerticesFloorsUnitNames(){
            return this.guideVerticesFloors.map( f => f.unit.name )
        },
        guideIndoorSegments(){
            const indoorGroups = [];
            const otherSegments = [];
            let tempIndoorGroupBgs = new Set();
            let ignoreNextGroup;
            let tempGroup = [], tempIndoorGroup = [];
            const tempFromOrToOutdoor = [];
            let isPrevStepOutdoor = false;

            console.debug('FS guideVerticesFloors:', this.guideVerticesFloorsUnitNames);

            this.guideVerticesFloors.forEach( f => {
                // If outdoor then tempGroup and any next group can't be separated
                if(f.floor.meta.is_outdoor){
                    isPrevStepOutdoor = true;
                    tempGroup.length && tempFromOrToOutdoor.push(tempGroup[tempGroup.length-1]);
                    if(tempFromOrToOutdoor.length > 2)
                        tempFromOrToOutdoor.shift();

                    tempIndoorGroup.push(...tempGroup);

                    // Push or merge group with other groups what has same bgIds
                    if(tempIndoorGroup.length){
                        indoorGroups.push(
                            {
                                'indoor': tempIndoorGroup,
                                'outdoor2IndoorSiblings': tempFromOrToOutdoor,
                                'bgIds': Array.from(tempIndoorGroupBgs)
                            }
                        );
                    }

                    tempGroup = [];
                    tempIndoorGroup = [];
                    tempIndoorGroupBgs = new Set();
                    return
                }

                tempGroup.push(f);
                if(isPrevStepOutdoor)
                {
                    tempGroup.length && tempFromOrToOutdoor.push(f);
                    if(tempFromOrToOutdoor.length > 2)
                        tempFromOrToOutdoor.shift();
                    isPrevStepOutdoor = false;
                }
                tempIndoorGroupBgs.add(f.floor.building_group_id);

            });

            tempIndoorGroup.push(...tempGroup);

            !isPrevStepOutdoor && tempFromOrToOutdoor.shift();
            // Push or merge last group with other groups what has same bgIds
            if(tempIndoorGroup.length)
                indoorGroups.push(
                    {
                        'indoor': tempIndoorGroup,
                        'outdoor2IndoorSiblings': tempFromOrToOutdoor,
                        'bgIds': Array.from(tempIndoorGroupBgs)
                    }
                );
            console.debug('FS: IndoorGroups', indoorGroups);
            return indoorGroups;
        },
        guideSegmentsFloors(){
            const indoorGroups = this.showOutdoor ?
                this.guideIndoorSegmentsWithCombinedPartsInSameBg :
                this.guideIndoorSegments;

            indoorGroups.forEach( (ig, index, groups) => {
                const acc = ig.indoor.reduce( (acc, f) => {
                    acc.bgs.add(f.floor.building_group_id);
                    acc.levels.add(f.floor.level);
                    return acc
                }, { bgs: new Set(), levels: new Set() }
                );
                groups[index].bgs = [...acc.bgs];
            });

            //For each group calculate entrances levels & min & max, bgs,
            indoorGroups.forEach( (ig, index, groups) => {

                const acc = ig.indoor.reduce( (acc, f) => {
                    acc.bgs.add(f.floor.building_group_id);
                    acc.levels.add(f.floor.level);
                    return acc
                }, { bgs: new Set(), levels: new Set() }
                );
                groups[index].bgs = [...acc.bgs];
                groups[index].levels = [...acc.levels];
                groups[index].verticalDirection = groups[index].levels[1] ? groups[index].levels[1]-groups[index].levels[0] : 0;

                // Find entrance levels for current floors group
                const groupEntranceLevels = [];
                for( const [bg, entranceLevels] of Object.entries(this.bgEntranceLevels) )
                {
                    groups[index].bgs.includes(bg) && groupEntranceLevels.push(...entranceLevels )
                }
                //Save min & max entrance levels for current group
                const indoor2outdoorLevels = _.uniq(groups[index].outdoor2IndoorSiblings.map( f => f.meta.level));
                groups[index].indoor2outdoorLevels = indoor2outdoorLevels;
                groups[index].entranceLevels = _.uniq(groupEntranceLevels);
                groups[index].maxEntranceLevel = Math.max(...groupEntranceLevels);
                groups[index].minEntranceLevel = Math.min(...groupEntranceLevels);
                groups[index].max2OutdoorLevel = Math.max(...indoor2outdoorLevels);
                groups[index].min2OutdoorLevel = Math.min(...indoor2outdoorLevels);
                groups[index].steps = this.path.steps.reduce(
                    (acc, s, index) => {
                        ig.indoor.some( f => f.id === s.floor_id ) && acc.push(index)
                        return acc
                    }, []);
            });

            console.debug('FS: indoorGroups', indoorGroups.map( g => (
                {
                    'indoor': g.indoor.map( f => f.unit.name),
                    'bgs': g.bgs,
                    'indoor2outdoorLevels': g.indoor2outdoorLevels,
                    'outdoor2IndoorSiblings': g.outdoor2IndoorSiblings.map( f => f.unit.name ),
                    'verticalDirection':g.verticalDirection,
                    'maxEntranceLevel': g.maxEntranceLevel,
                    'minEntranceLevel': g.minEntranceLevel,
                    'max2OutdoorLevel': g.max2OutdoorLevel,
                    'min2OutdoorLevel': g.min2OutdoorLevel,
                    'entranceLevels': g.entranceLevels,
                    'steps': g.steps,
                })
            ));

            return indoorGroups;
        },

        guideIndoorSegmentsWithCombinedPartsInSameBg(){
            let indoorGroups = [];
            const otherSegments = [];
            let tempIndoorGroupBgs = new Set();
            let ignoreNextGroup;
            let tempGroup = [], tempIndoorGroup = [];
            const tempFromOrToOutdoor = [];
            let isPrevStepOutdoor = false;

            console.debug('FS guideVerticesFloors:', this.guideVerticesFloorsUnitNames);

            this.guideVerticesFloors.forEach( f => {
                // If outdoor then tempGroup and any next group can't be separated
                if(f.floor.meta.is_outdoor){
                    isPrevStepOutdoor = true;
                    tempGroup.length && tempFromOrToOutdoor.push(tempGroup[tempGroup.length-1]);
                    if(tempFromOrToOutdoor.length > 2)
                        tempFromOrToOutdoor.shift();

                    tempIndoorGroup.push(...tempGroup);

                    // Push or merge group with other groups what has same bgIds
                    if(tempIndoorGroup.length){
                        indoorGroups = pushByComplexIndex(
                            indoorGroups,
                            'bgIds',
                            {
                                'indoor': tempIndoorGroup,
                                'outdoor2IndoorSiblings': tempFromOrToOutdoor,
                                'bgIds': Array.from(tempIndoorGroupBgs)
                            }
                        );
                    }

                    tempGroup = [];
                    tempIndoorGroup = [];
                    tempIndoorGroupBgs = new Set();
                    isPrevStepOutdoor = true;
                    return
                }

                tempGroup.push(f);
                if(isPrevStepOutdoor)
                {
                    tempGroup.length && tempFromOrToOutdoor.push(f);
                    if(tempFromOrToOutdoor.length > 2)
                        tempFromOrToOutdoor.shift();
                    isPrevStepOutdoor = false;
                }
                tempIndoorGroupBgs.add(f.floor.building_group_id);
            });
            tempIndoorGroup.push(...tempGroup);
            !isPrevStepOutdoor && tempFromOrToOutdoor.shift();

            // Push or merge last group with other groups what has same bgIds
            if(tempIndoorGroup.length)
                indoorGroups = pushByComplexIndex(
                    indoorGroups,
                    'bgIds',
                    {
                        'indoor': tempIndoorGroup,
                        'outdoor2IndoorSiblings': tempFromOrToOutdoor,
                        'bgIds': Array.from(tempIndoorGroupBgs)
                    }
                );
            console.debug('FS: mergedIndoorGroups', indoorGroups)
            return indoorGroups;
        },

        bgEntranceLevels(){
            const bgEntranceLevels = this.entrances.reduce( (acc, e) => pushUnique2ObjectProp(acc, e.floor.building_group_id, e.floor.level), {})
            console.debug('FS bgEntranceLevels: ', bgEntranceLevels);
            return bgEntranceLevels;
        },
        bgPathLevels(){ //bgPathLevels
            const bgPathLevels = {}
            this.guideSegmentsFloors.forEach( group => {
                const levels  = new Set();
                const bgs  = new Set();

                group.indoor.forEach( (f) => {
                    levels.add(f.floor.level)
                    bgs.add(f.floor.building_group_id)
                })

                bgs.forEach(
                    bg => levels.forEach(
                        l => pushUnique2ObjectProp(bgPathLevels, bg, l)
                    )
                )

            });
            console.debug('FS bgPathLevels:', bgPathLevels)
            return bgPathLevels
        },
        bgLevels2Expand(){
            const checkHasNoEntrances = this.showOutdoor && this.getGuideConfig('separate_floors_in_segment_when')?.includes('hasNoEntrances');
            const checkHasNoGroundFloors = this.showOutdoor &&  this.getGuideConfig('separate_floors_in_segment_when')?.includes('hasNoGroundFloors');
            const checkHasNoConnectionsWithOutdoor = this.showOutdoor && this.getGuideConfig('separate_floors_in_segment_when')?.includes('hasNoConnectionsWithOutdoor');
            const checkCanExpandDownWhenCovered = this.getGuideConfig('separate_floors_in_segment_when')?.includes('expand_down_when_covered');

            const bgLevels2Expand = {};
            this.guideSegmentsFloors.forEach( group => {
                const levels  = new Set();
                const bgs  = new Set();
                const entranceLevelsExpandBottom = new Set();

                group.indoor.forEach( (f) => {
                    levels.add(f.floor.level);
                    bgs.add(f.floor.building_group_id);

                });

                console.debug("FS: bgLevels2ExpandBefore", levels);
                const sortedLevels = Array.from(levels).sort();
                sortedLevels.forEach(
                    (l,i,s) => {
                        const isUndergroundLevel = l < group.minEntranceLevel;
                        const hasGuideLevelAbove = Array.from(levels).some(sl => sl > l);
                        const hasEntranceAbove = checkHasNoEntrances && group.entranceLevels.some( el => el > l);
                        const isEntranceLevel = group.entranceLevels.includes(l);
                        const hasConnectionsWithOutdoorOnTop = checkHasNoConnectionsWithOutdoor && group.indoor2outdoorLevels.some( el => el > l);
                        const isConnectedWithOutdoor = checkHasNoConnectionsWithOutdoor && group.indoor2outdoorLevels.includes( l);
                        const canExpandDownWhenCovered = checkCanExpandDownWhenCovered && hasGuideLevelAbove && !isConnectedWithOutdoor;

                        const canExpand = !hasEntranceAbove && !hasConnectionsWithOutdoorOnTop;

                        if(
                            !canExpand &&
                            this.showOutdoor &&
                            !isUndergroundLevel
                        )
                        {
                            // Save entranceLevelsExpandBottom to exclude them from minEntranceLevel
                            if(isEntranceLevel && canExpandDownWhenCovered)
                            {
                                entranceLevelsExpandBottom.add(l);
                            }
                            else {
                                levels.delete(l);
                                levels.add(l+1);
                                sortedLevels.push(l+1);
                                s = sortedLevels.sort();
                            }
                        }
                    });

                // let minSegmentEntranceLevel = Number.POSITIVE_INFINITY;
                const maxExcludedLevel = Math.max(...entranceLevelsExpandBottom);
                let minEntranceLevel = 0;
                if(entranceLevelsExpandBottom.size){
                    minEntranceLevel = maxExcludedLevel+1;
                }
                else{
                    minEntranceLevel = group.minEntranceLevel;
                }
                /*                else{
                    minEntranceLevel = Math.min(bgMinEntranceLevel, group.minEntranceLevel);
                }*/

                group.bgs.forEach( bg => {
                    //const bgMinEntranceLevel = _.get(bgLevels2Expand, `['minEntranceLevel'][${bg}]`, Number.POSITIVE_INFINITY);
                    const bgMin2OutdoorLevel = _.get(bgLevels2Expand, `['min2OutdoorLevel'][${bg}]`, Number.POSITIVE_INFINITY);
                    const isEntranceLevelExcluded = entranceLevelsExpandBottom.has(group.minEntranceLevel);

                    _.set(bgLevels2Expand, `['minEntranceLevel'][${bg}]`, minEntranceLevel);

                    /*if( group.minEntranceLevel < bgMinEntranceLevel && !isEntranceLevelExcluded)
                        _.set(bgLevels2Expand, `['minEntranceLevel'][${bg}]`, group.minEntranceLevel);*/
                    if( group.min2OutdoorLevel <  bgMin2OutdoorLevel)
                        _.set(bgLevels2Expand, `['min2OutdoorLevel'][${bg}]`, group.min2OutdoorLevel);

                    if(!bgLevels2Expand[bg])
                        bgLevels2Expand[bg] = [];

                    bgLevels2Expand[bg].push(...levels);
                    bgLevels2Expand[bg] = _.uniq(bgLevels2Expand[bg]);
                });
            });
            console.debug('FS bgLevels2Expand: ', bgLevels2Expand);
            return bgLevels2Expand
        },

        guidePortals(){
            return this.guideSteps.reduce( (acc, s, index) => {
                const portal = s.parameters.find(p => p.name === 'portal');
                if(!portal || !['up','down','out','in'].includes(s.direction)) return  acc;
                const portalObject = this.mapViewer.mapData.objects.find( o => o.id === portal.value && s.floor_id === o.floor.id );
                if(portalObject && index !== 0)
                    acc.push({ index, step: s, portal: portalObject})
                return acc
            },[])
        },
        guidePortalUnits(){
            return this.guidePortals.map( p => p.portal.unit.name )
        },
        guideStepsInFocus(){
            const i = this.activeGuideStepIndex
            return i < this.guideSteps.length -1 ? [i, i+1] : [i]
        },
        guideOutdoorFloors(){
            return this.pathFloors.filter( f => f.meta.is_outdoor )
        },
        guideInitialVerticalDirection(){
            const vDir = this.guideSegmentsFloors[0]?.verticalDirection;
            if(!vDir) return 'none';
            if(vDir > 0) return 'up';
            return 'down';
        },
        activeGuideStep(){
            return this.guideSteps[this.activeGuideStepIndex]
        },

        isDistanceEnough() {
            return this.guideSteps.length < 3 ? this.distance > this.getMapConfigProp('distance_befor_hide_active_step', 100) : true
        },
        activeGuideStepRadius(){
            const stepZoomMargin = this.getGuideCameraConfig('guide_step_view_margin', 0.0)
            const radius = this.activeGuideStep.world_distance > 30 ? this.activeGuideStep.world_distance : 20;
            const maxRadius = this.getGuideCameraConfig('guide_step_max_world_distance', 10000) * (1 - stepZoomMargin);

            return radius > maxRadius ? maxRadius : radius
        },

        activeGuideStepSegment(){
            return this.guideSegmentsFloors.find( g => g.steps.includes(this.activeGuideStepIndex ))
        },
        activeGuideStepSegmentFloors(){
            return this.activeGuideStepSegment?.indoor || []
        },
        activeGuideStepSegmentFloorsUnitNames(){
            return this.activeGuideStepSegmentFloors.map( f => f.unit.name );
        },
        activeGuideStepSegmentBuildingGroups(){
            return this.activeGuideStepSegment?.bgs || []
        },
        activeGuideStepSegmentLevels(){
            return this.activeGuideStepSegment?.levels || []
        },
        activeGuideStepSegmentAnchorFloors(){
            return this.anchorFloors.filter( f =>
                this.activeGuideStepSegmentBuildingGroups.includes(f.floor.building_group_id) &&
                this.activeGuideStepSegmentLevels.includes(f.floor.level)
            )
        },
        activeGuideStepSegmentAnchorFloorsUnitNames(){
            return this.activeGuideStepSegmentAnchorFloors.map( f => f.unit.name )
        },

        activeGuideStepFloors(){
            if( this.activeGuideStep?.floors )
                return this.activeGuideStep?.floors.map( fid => this.pathFloors.find( f => f.id === fid));
            // return this.pathFloors.filter( f => this.activeGuideStep?.floors.includes(f.id) );
            return []
        },
        activeGuideStepFloorsUnitName(){
            return this.activeGuideStepFloors.map(f => f.unit.name);
        },

        activeGuideStepLevelFloors(){
            // return this.activeGuideStepVisibleFloors.filter( f => this.activeGuideStepLevels.includes( f.meta.level ) )
            return this.activeGuideStepVisibleFloors.filter( f => this.filteredGuideStepLevels.includes( f.meta.level ) )
        },
        activeGuideStepLevelFloorsUnitNames(){
            return this.activeGuideStepLevelFloors.map( f => f.unit.name )
        },
        activeGuideStepLevels(){
            return [...new Set(this.activeGuideStepFloors.map( f => f.meta.level ))];
        },
        filteredGuideStepLevels(){
            const dir = ['up', 'down'];
            const hasSomething = this.activeGuideStepLevels.length;
            const hasDir = dir.includes(this.activeGuideStep.direction);

            return hasSomething && hasDir ? [this.activeGuideStepLevels[0]] : this.activeGuideStepLevels;
        },

        activeGuideStepBuildingGroups(){
            console.debug("activeGuideStepFloors: ", this.activeGuideStepFloorsUnitName)
            return Array.from(this.activeGuideStepFloors.reduce(
                (acc, f) =>
                    f.floor.building_group_id ? acc.add(f.floor.building_group_id) : acc, new Set()
            ));
        },

        activeGuideStepUndergroundFloors(){
            return this.activeGuideStepFloors.filter( f =>
                this.bgLevels2Expand.minEntranceLevel[f.floor.building_group_id] &&
                f.meta.level < this.bgLevels2Expand.minEntranceLevel[f.floor.building_group_id]
            )
        },
        activeGuideStepOutdoorFloors(){
            return this.activeGuideStepFloors.filter(f => f.meta.is_outdoor)
        },
        activeGuideStepVisibleFloors(){
            const visibleFloors = []
            if(this.showOutdoor)
                return this.visibleFloors;

            visibleFloors.push( ...this.activeGuideStepSegmentFloors);

            if(!this.isUndergroundGuide)
                visibleFloors.push( ...this.activeGuideStepSegmentAnchorFloors);

            return _.uniqBy(visibleFloors, 'id')
        },
        activeGuideStepVisibleFloorsUnitNames(){
            return this.activeGuideStepVisibleFloors.map( f => f.unit.name )
        },
        doesActiveGuideStepHasFloorsAbove(){
            const af = this.activeGuideStepFloors[0];
            return af && this.visibleFloors.find( f =>
                af.meta.building_index === f.meta.building_index &&
                af.meta.level < f.meta.level
            )
        },
        activeGuideStepCameraTilt(){
            return ['up', 'down'].includes(this.activeGuideStep.direction) || this.doesActiveGuideStepHasFloorsAbove || this.activeGuideStepUndergroundFloors.length
                ? 22  : 45
        },

        startGuideStep(){
            return this.guideSteps[0]
        },
        startGuideStepFloor(){
            return this.pathFloors.find( f => f.id === this.startGuideStep.floor_id )
        },

        floors(){
            return this.mapViewer.mapData.objects.filter( o => o.type === 'floor');
        },
        outdoorFloors(){
            return this.floors.filter( f => f.meta.is_outdoor )
        },
        outdoorFloorsUnitNames(){
            return this.outdoorFloors.map( f => f.unit.name )
        },

        groundFloors(){
            return this.floors.filter( (f) => f.meta.is_ground )
        },
        buildingsGroundLevels(){
            return this.groundFloors.reduce( (acc, f) => pushUnique2ObjectProp(acc, f.meta.building_index, f.meta.level), {})
        },
        buildingsGroupGroundLevels(){
            return this.groundFloors.reduce( (acc, f) => pushUnique2ObjectProp(acc, f.meta.building_group_id, f.meta.level),{})
        },

        buildings(){
            return this.mapViewer.mapData.objects.filter( o => o.type === 'building' && !o.meta.is_outdoor);
        },
        outdoorBuildings(){
            return this.mapViewer.mapData.objects.filter( o => o.type === 'building' && o.meta.is_outdoor);
        },
        outdoorBuildingIDs(){
            return this.outdoorBuildings.map( b => b.meta.building_index )
        },

        allLevels(){
            return Array.from(this.floors.reduce((acc,f) => acc.add(f.meta.level), new Set()));
        },
        anchorFloors(){
            return this.floors.filter( f => f.meta.anchor)
        },
        anchorFloorsUnitNames(){
            return this.anchorFloors.map( f => f.unit.name );
        },
        pathFloors(){
            if( !!this.path.floors )
                return this.floors.filter( o => this.path.floors.includes(o.id) );
            return []
        },
        pathFloorsUnitNames(){
            return this.pathFloors.map(f => f.unit.name);
        },
        pathIndoorFloors(){
            return this.pathFloors.filter( o => !o.meta.is_outdoor );
        },
        pathIndoorFloorsUnitNames(){
            return this.pathIndoorFloors.map(f => f.unit.name);
        },

        pathBuildingIDs(){
            return [...new Set([...this.pathFloors.map( f => f.meta.building_index )])]
        },
        pathBuildingsMaxLevels(){
            return this.pathFloors.reduce((acc, f) => {
                if( typeof acc[f.meta.building_index] === "undefined" || f.meta.level > acc[f.meta.building_index])
                    acc[f.meta.building_index] = f.meta.level
                return acc
            }, {} )
        },
        pathBuildingsMinLevels(){
            return this.pathFloors.reduce((acc, f) => {
                if( typeof acc[f.meta.building_index] === "undefined" || f.meta.level < acc[f.meta.building_index])
                    acc[f.meta.building_index] = f.meta.level
                return acc
            }, {} )
        },

        pathBuildingGroupsIDs(){
            return Array.from(this.pathFloors.reduce((acc,f) => acc.add(f.floor.building_group_id), new Set()));
        },

        pathBuildingGroupsAnchorFloors(){
            return this.anchorFloors.filter( f => f.meta.level === 0  && this.pathBuildingGroupsIDs.includes(f.floor.building_group_id) )
        },
        pathBuildingGroupsAnchorFloorUnitNames(){
            return this.pathBuildingGroupsAnchorFloors.map( f => f.unit.name)
        },

        pathBuildingGroupsFloors(){
            return this.floors.filter( f => this.pathBuildingGroupsIDs.includes(f.floor.building_group_id) );
        },
        pathBuildingGroupsFloorsUnitNames(){
            return this.pathBuildingGroupsFloors.map( f => f.unit.name);
        },

        notInPathBuildingGroups(){
            return this.buildings.reduce( (acc, b) =>
                !this.pathBuildingGroupsIDs.includes(b.meta.building_group_id) && acc.add(b.meta.building_group_id) || acc, new Set)
        },
        notInPathBuildingGroupsBuildings(){
            return this.buildings.filter( b => !this.pathBuildingGroupsIDs.includes(b.meta.building_group_id) )
        },
        notInPathBuildingGroupsBuildingsIDs(){
            return (this.showOutdoor && this.notInPathBuildingGroupsBuildings.map( b => b.meta.building_index)) || []
        },

        pathIntermediateTransparentFloors(){
            return this.pathBuildingsIntermediateLevelsFloors.reduce( (acc, f) =>
            {
                /*
                   if(this.showOutdoor &&
                    (
                        this.bgEntranceLevels[f.floor.building_group_id].includes(f.meta.level)  ||
                    f.meta.anchor ||
                    f.meta.is_ground
                    )
                )
                    return  acc*/

                acc.push(f)

                return acc
            }, [] );
        },
        pathIntermediateTransparentFloorsUnitNames(){
            return this.pathIntermediateTransparentFloors.map( f => f.unit.name )
        },

        transparentCoversBuildingsIDs(){
            return this.shouldOutdoorBeTransparent ? this.notInPathBuildingGroupsBuildingsIDs : [];
        },
        greyedCoversBuildingsIDs(){
            const greifyCovers = this.getGuideConfig('greyify_covers', 'none') === 'not_in_guide_building_groups';
            return greifyCovers ? this.notInPathBuildingGroupsBuildingsIDs : [];
        },

        pathTransparentFloorsIDs(){
            const transparentFloors = [];
            this.shouldOutdoorBeTransparent && transparentFloors.push(...this.outdoorFloorsUnitNames);

            if(this.guideInitialVerticalDirection !== 'down')
                transparentFloors.push( ...this.bgLevelsThatCoverPathFloorsUnitNames );
            else
                transparentFloors.push( ...this.pathIntermediateTransparentFloorsUnitNames );

            return transparentFloors
        },
        pathStepTransparentFloorsIDs(){
            const transparentFloors = [];
            !this.doseActivePathStepHasOutdoorFloors && transparentFloors.push(...this.outdoorFloorsUnitNames);
            transparentFloors.push( ...this.pathIntermediateTransparentFloorsUnitNames );

            return transparentFloors
        },

        doseActivePathStepHasUndergroundFloors(){
            return this.activeGuideStepFloors.find( f => f.meta.level < 0)
        },
        doseActivePathStepHasOutdoorFloors(){
            return this.activeGuideStepFloors.find( f => f.meta.is_outdoor)
        },

        bgLevelsData(){
            const heightOffsets = {};
            const bgLevelsThatCoverPath = {};
            let hasExpandedUndergroundLevels = false;

            for (const [bgId, levels] of Object.entries(this.bgPathLevels)) {

                const min2ExpandLevel = Math.min(...this.bgLevels2Expand[bgId]);
                const minGroundLevel = this.bgLevels2Expand.minEntranceLevel[bgId];
                const minBgEntranceLevel = this.minLevel;

                let floorHeightOffset = 0;
                let undergroundFloorHeightOffset = 0;
                let isCoveringPath = false;

                for(let j = minBgEntranceLevel; j < minGroundLevel; j++) {
                    !levels.includes(j) && pushUnique2ObjectProp(bgLevelsThatCoverPath,bgId,j);
                }

                //Iterate expandable levels from ground to the top
                for(let l = minGroundLevel; l <= this.pathMaxLevel; l++){
                    set2ObjectProp(heightOffsets, bgId, l, floorHeightOffset);
                    (isCoveringPath || !levels.includes(l)) && pushUnique2ObjectProp(bgLevelsThatCoverPath,bgId,l);

                    //if this is next level what we need to expand then increase offset
                    if(this.bgLevels2Expand[bgId]?.includes(l)){
                        floorHeightOffset += this.floorSeparationDistance;
                        isCoveringPath = false;
                    }
                    else{
                        if(levels.includes(l))
                        {
                            isCoveringPath = true;
                        }
                    }
                }

                //Iterate expandable levels from bottom ground level to bottom underground
                if(min2ExpandLevel < minGroundLevel)
                {
                    for (let ul = minGroundLevel - 1; ul >= this.pathIndoorMinLevel; ul--) {

                        //if this is next level what we need to expand then increase offset
                        if (this.bgLevels2Expand[bgId]?.includes(ul)) {
                            undergroundFloorHeightOffset -= this.floorSeparationDistance;
                            hasExpandedUndergroundLevels = true;
                        }

                        set2ObjectProp(heightOffsets, bgId, ul, undergroundFloorHeightOffset);
                    }
                }
            }
            return { heightOffsets, bgLevelsThatCoverPath, hasExpandedUndergroundLevels };
        },
        bgLevelsHeightOffsets(){
            console.debug('FS: bgLevelsHeightOffsets', this.bgLevelsData.heightOffsets)
            return this.bgLevelsData.heightOffsets
        },
        bgLevelsThatCoverPath(){
            return this.bgLevelsData.bgLevelsThatCoverPath;
        },
        bgLevelsThatCoverPathFloors(){
            return this.visibleFloors.filter( f =>
                this.bgLevelsThatCoverPath[f.floor.building_group_id]?.includes(f.floor.level)
            )
        },
        bgLevelsThatCoverPathFloorsUnitNames(){
            return this.bgLevelsThatCoverPathFloors.map( f => f.unit.name );
        },

        hasExpandedUndergroundLevels(){
            return this.bgLevelsData.hasExpandedUndergroundLevels;

            /*  for (const [bgId, levels] of Object.entries(this.bgPathLevels)) {

                const min2ExpandLevel = this.pathIndoorMinLevel;
                const minGroundLevel = this.bgLevels2Expand.minEntranceLevel[bgId]

                //Iterate expandable levels from bottom ground level to bottom underground
                if(min2ExpandLevel < minGroundLevel)
                    for(let l = minGroundLevel-1; l >= this.pathIndoorMinLevel; l--){
                        //if this is next level what we need to expand then increase offset
                        if(this.bgLevels2Expand[bgId]?.includes(l))
                            return true
                    }
            }
            return false*/
        },
        floorsHeightOffset(){
            const c = this.visibleFloors.reduce( (acc, f) => {
                // If bg is not in a list
                if(!this.bgLevelsHeightOffsets[f.floor.building_group_id])
                    return acc;

                // If intermediate floor is anchor or ground floor, we should not rise it
                // if( !this.pathFloorsUnitNames.includes(f.unit.name) && (f.meta.anchor || f.meta.is_ground ) )
                // return acc

                acc[f.unit.name] = this.bgLevelsHeightOffsets[f.floor.building_group_id][f.floor.level] || 0

                return acc;
            }, {});
            console.debug('FS: floorsHeightOffset', c)
            return c
        },
        pathLevels(){
            return Array.from(this.pathFloors.reduce((acc,f) => acc.add(f.meta.level), new Set()));
        },
        pathIndoorLevels() {
            return Array.from(this.pathIndoorFloors.reduce((acc,f) => acc.add(f.meta.level), new Set()));
        },
        separateLevels(){
            return this.pathIndoorLevels.filter( l => this.pathIndoorLevels.includes(l+1));
        },
        pathLevelsFloors(){
            return this.floors.filter( f => this.pathLevels.includes(f.meta.level) );
        },
        pathLevelsFloorsUnitNames(){
            return this.pathLevelsFloors.map( f => f.unit.name );
        },

        pathIntermediateLevels(){
            return this.allLevels.filter( l => !this.pathLevels.includes(l) /*&& l > this.pathIndoorMinLevel && l < this.pathMaxLevel*/ )
        },
        pathBuildingsIntermediateLevelsFloors(){
            return this.floors.filter( f =>
                this.pathIntermediateLevels.includes(f.meta.level) &&
                this.pathBuildingIDs.includes(f.meta.building_index) &&
                this.pathBuildingsMaxLevels[f.meta.building_index] > f.meta.level
                // this.pathBuildingsMinLevels[f.meta.building_index] < f.meta.level &&
            );
        },
        pathIntermediateFloors(){
            return this.floors.filter( f =>
                this.pathIndoorFloors.find(
                    pif => pif.meta.building_index === f.meta.building_index
                        && pif.meta.level > f.meta.level
                )
                &&
                this.pathIndoorFloors.find(
                    pif => pif.meta.building_index === f.meta.building_index
                        && pif.meta.level < f.meta.level
                )
                ||
                this.pathIndoorFloors.find(
                    pif => pif.meta.building_index === f.meta.building_index
                        && pif.meta.level > f.meta.level
                )
            );
        },
        pathIntermediateFloorsUnitNames(){
            return this.pathIntermediateFloors.map( f => f.unit.name );
        },
        pathBuildingsIntermediateLevelsFloorsUnitNames(){
            return this.pathBuildingsIntermediateLevelsFloors.map( f => f.unit.name );
        },

        pathBuildingGroupsLowestGroundLevelFloors(){
            return this.floors.filter( f =>
                this.pathIndoorFloors.find(
                    pif =>
                        pif.floor.level === f.meta.level &&
                        pif.floor.building_group_id === f.floor.building_group_id &&
                        f.meta.level === this.bgLevels2Expand.minEntranceLevel[f.floor.building_group_id]
                )
            )
        },

        pathMaxLevel(){
            return Math.max(...this.pathLevels);
        },
        pathMinLevel(){
            return Math.min(...this.pathLevels);
        },
        pathIndoorMinLevel(){
            return Math.min(...this.pathIndoorLevels);
        },
        pathIndoorMaxLevel(){
            return Math.max(...this.pathIndoorLevels);
        },

        maxLevel(){
            return Math.max(...this.allLevels);
        },
        minLevel(){
            return Math.min(...this.allLevels);
        },

        additionalFloors(){
            return [
                // ...this.anchorFloors,
                ...this.pathIntermediateFloors,
                ...this.pathBuildingGroupsAnchorFloors,
                ...this.pathBuildingGroupsLowestGroundLevelFloors,
            ]
        },
        additionalFloorsWhatNotCoverPath(){
            const pathFloors = this.pathFloors
            return this.additionalFloors.filter(af => {
                const hasPathFloorBelow = pathFloors.find(pf =>
                    pf.meta.building_index === af.meta.building_index &&
                    pf.meta.level+1 === af.meta.level
                );
                const isBelowFloorExpanded =
                    this.bgLevels2Expand[af.floor.building_group_id] &&
                    this.bgLevels2Expand[af.floor.building_group_id].includes(af.meta.level - 1);

                return !hasPathFloorBelow || isBelowFloorExpanded;
            })
        },
        visibleFloors(){
            const visibleFloors = [
                //...this.pathBuildingGroupsAnchorFloors,
                //...this.pathBuildingsIntermediateLevelsFloorsUnitNames,
            ];

            if(this.showOutdoor){
                visibleFloors.push(...this.pathFloors);
                visibleFloors.push(...this.outdoorFloors);
                visibleFloors.push(...this.additionalFloorsWhatNotCoverPath);
            }
            else{
                // guide step view
                visibleFloors.push( ...this.activeGuideStepSegmentFloors);

                if(!this.isUndergroundGuide)
                    visibleFloors.push( ...this.activeGuideStepSegmentAnchorFloors);
            }

            return _.uniqBy(visibleFloors, 'id')
        },

        visibleFloorsUnitNames(){
            return this.visibleFloors.map( f => f.unit.name )
        },
        highlightedFloors(){
            const greifyProp = this.getGuideConfig('greyify_floors', '');
            const greifySecondarySegments = this.getGuideConfig('greyify_secondary_segments', false);
            let greifyFloors = [];

            switch( greifyProp ){
            case 'not_in_guide':
                greifyFloors = this.pathFloors;
                break;
            case 'not_in_guide_building_groups':
                greifyFloors = this.pathBuildingGroupsFloors;
                break;
            case 'none':
            default:
                greifyFloors = this.visibleFloors;
                break;
            }
            // If has outdoor segment between indoor segments highlight indoor (greyify outdoor)
            if(greifySecondarySegments && this.isInOutInGuide)
                return greifyFloors.filter(f => f.meta.is_outdoor);

            // If has indoor segments between outdoor segments highlight outdoor (greyify indoor)
            if(greifySecondarySegments && this.isOutInOutGuide)
                return greifyFloors.filter(f => !f.meta.is_outdoor)

            return greifyFloors;
        },
        highlightedFloorsUnitNames(){
            return this.highlightedFloors.map( f => f.unit.name )
        },

        floorSeparationDistance(){
            return this.isUndergroundGuide
                ? this.getGuideCameraConfig('separate_underground_floor_distance', 60)
                : this.getGuideCameraConfig('separate_floor_distance', 40)
        },
        // Path Steps
        startObject(){
            return this.path?.from?.id && this.mapViewer.mapData.objects.find( o => o.id === this.path.from );
        },
        endObject(){
            return this.path?.to?.id && this.mapViewer.mapData.objects.find( o => o.id === this.path.to.id );
        },
        endUnit(){
            return this.path?.to?.unit
        },
        isUndergroundGuide(){
            return this.pathMinLevel < this.minOutdoorLevel;
        },

        isMobile(){
            return document.location.href.includes('mobile')
        },

        showOutdoor(){
            return Boolean(this.activeGuideStep && this.mapViewer.activeView === 'GuideStepView' ? this.activeGuideStepOutdoorFloors.length : true )
            //typeof this.pathIndoorLevels.find( l => this.outdoorLevels.includes(l) ) !== 'undefined'
        },
        outdoorLevels(){
            return this.getMapConfigProp('global_map.outdoor_levels', [0])
        },
        minOutdoorLevel(){
            return Math.min(...this.outdoorLevels);
        },
        maxOutdoorLevel(){
            return Math.max(...this.outdoorLevels);
        },
        shouldOutdoorBeTransparent(){
            return this.isUndergroundGuide || this.hasExpandedUndergroundLevels
        },
        /* targetZoneServices(){
            const tzCatId = this.getMapConfigProp('categories.target_zone_service_category')
            if(!tzCatId){
                // TODO: Add/replace with sentry logging
                console.error('Target zone category is not defined. Set it in map settings `categories.target_zone_service_category`')
                return []
            }
            return this.md.objects.filter( o => o.type === 'service' && o.lists.categories && o.lists.categories.includes(tzCatId) && o.files.icon )
        },
        targetZoneServicesUnitNames(){
            return this.targetZoneServices.map( o => o.unit.name )
        },*/

        isDebug(){
            return document.location.href.includes('localhost');
        },
        shouldShowFinishStepBubble() {
            if(!this.endObject || !this.endUnit)
                return  false;

            const activeStepFloor = this.guideSteps[this.activeGuideStepIndex].floor_id
            const finishStepFloor = this.guideSteps[this.guideSteps.length -1].floor_id

            return activeStepFloor === finishStepFloor
        },
        shouldShowBubblesDuringTransition(){
            return this.getGuideConfig('show_bubbles_during_transitions', true)
        },
        shouldShowBubblesConfig(){
            return this.getGuideConfig('show_bubbles', true)
        },
        shouldShowBubbles(){
            return this.isViewInTransition ? this.shouldShowBubblesDuringTransition : this.shouldShowBubblesConfig;
        },
        activeGuideSteps(){
            return [this.activeGuideStepIndex];
        },
        isGuideView(){
            return this.activeGuideStepIndex === null &&
                this.path &&
                this.mapViewer.activeView === 'GuideView' &&
                this.$route.name.includes('guide');
        },
        isGuideStepView(){
            return this.activeGuideStepIndex !== null &&
                this.path &&
                this.mapViewer.activeView === 'GuideStepView' &&
                this.$route.name.includes('guide');
        },

        isInOutInGuide(){
            return this.startObject?.floor.meta.is_outdoor &&
                this.endObject?.floor.meta.is_outdoor &&
                this.guideIndoorSegments.length
        },
        isOutInOutGuide(){
            return !this.startObject?.floor.meta.is_outdoor &&
                !this.endObject?.floor.meta.is_outdoor &&
                this.guideOutdoorFloors.length
        },

        notCoveredGuideFloors(){
            return this.visibleFloors.filter( f => {
                const hasFloorAbove = this.visibleFloors.find( fa =>
                    fa.meta.level > f.meta.level &&
                    fa.meta.building_index === f.meta.building_index
                ) ;
                const isExpanded = (this.bgLevels2Expand[f.floor.building_group_id] || []).includes(f.meta.level);

                return !hasFloorAbove || hasFloorAbove && isExpanded;
            });
        },
        notCoveredGuideFloorsUnitNames(){
            return this.notCoveredGuideFloors.map( f => f.unit.name );
        },
        // -------------------- Icons -----------------------

        pathBuildingGroupsFloorsIcons(){
            //return false;
            /*const showIconsOn = this.getGuideConfig('show_icons_on', 'path_floors');

            let fUnitNames = [];
            switch (showIconsOn){
            case 'path_floors':
                fUnitNames = this.pathFloorsUnitNames;
                break;
            case 'not':
                break;
            }*/
            console.debug('FS: visibleFloors: ', this.visibleFloorsUnitNames)
            console.debug('FS: notCoveredGuideFloors: ', this.notCoveredGuideFloorsUnitNames)
            const fUnitNames = this.notCoveredGuideFloorsUnitNames;

            return this.objects.reduce((acc, obj, index) => {
                if( fUnitNames.includes(obj.floor.unit.name) ){
                    switch (obj.type){
                    case 'portal':
                    case 'label':
                        acc.push(`${obj.id}--${obj.unit.name}`)
                        break;
                    case 'tenant':
                    case 'service':
                        acc.push(obj.id)
                        break;
                    }
                }

                return acc;
            },[])
        },

    },

    // -------------------------------- Methods ---------------------------------------
    methods: {

        initEventListeners() {
            // this.$bus.$on(REBUILD_PATH, this.resetGuide);
            // this.$bus.$on(MAP_SHOW_PATH, this.onShowPath);
            // this.$bus.$on(START_GUIDE_OVERVIEW, this.resetGuide);
            // this.$bus.$on(SET_NORMALIZED_ZOOM, this.onZoom);
            this.$bus.$on(REMOVE_PATH, this.onRemovePath);
            this.$bus.$on(RESET_MAP_VIEW, this.resetViews);
            this.$bus.$on(UI_STEP_SELECTED, this.onGuideStepChange);
            this.$bus.$on(MAP_SHOW_PATH_STEPS, this.onGuideStepChange);
            this.$bus.$on(UI_STEP_BY_STEP_DIRECTIONS, this.setPathSteps);

            this.$bus.$on(SET_GYROSCOPE_STATE, this.onGyroStateChange)
        },
        deactivateEventListeners() {
            // this.$bus.$off(MAP_SHOW_PATH, this.onShowPath);
            // this.$bus.$off(SET_NORMALIZED_ZOOM, this.onZoom);
            this.$bus.$off(REMOVE_PATH, this.onRemovePath);
            this.$bus.$off(RESET_MAP_VIEW, this.resetViews);
            this.$bus.$off(UI_STEP_SELECTED, this.onGuideStepChange);
            this.$bus.$off(MAP_SHOW_PATH_STEPS, this.onGuideStepChange);
            this.$bus.$off(UI_STEP_BY_STEP_DIRECTIONS, this.setPathSteps);
            this.$bus.$off(SET_GYROSCOPE_STATE, this.onGyroStateChange)

            // this.$bus.$off(START_GUIDE_OVERVIEW, this.resetGuide);
            // this.$bus.$off(REBUILD_PATH, this.resetGuide);
        },
        onGyroStateChange(isGyroEnabled){
            this.canOrient = isGyroEnabled
        },

        onZoom(data){
            if(this.mapViewer.viewApi.camera_controller.normalized_zoom > 0.4){
                this.$bus.$emit(REMOVE_PATH, true)
                this.$emit('changeView','OutdoorView')
            }
        },
        onRemovePath(){
            this.mapViewer.viewApi.remove_path()
            this.mapViewer.viewApi.restore_base_color()

            this.$store.commit('setActiveStepIndex', null);
            this.$store.commit('setActiveStep', null);
            this.$store.commit('setActivePathToState', null);

            this.$emit('changeView','OutdoorView')
        },
        setPathSteps(path){
            this.$emit('changeView','GuideView')
        },
        onGuideStepChange(){
            this.$emit('changeView','GuideStepView')
            this.guideStepRenderTrigger+=1
        },
        setActiveGuideStep(guideStepIndex){
            this.$bus.$emit(UI_STEP_SELECTED, guideStepIndex)
        },
        getPortalIconSlug(portalType){
            return _.lowerFirst(portalType).slice(0,-1)
        },
        getStepIconUrl(index){
            const step = this.guideSteps[index]
            let key = step.direction
            if(['up', 'down', 'in', 'out'].includes(step.direction)){
                key = _.toLower(step.type) + '-' + key
            }
            const portalType = this.guideSteps[index]
            const res = this.mapViewer.mapData.config.icons.find( i => i.resource_id == key )
            if(!res){
                console.error(`Icon resource not found for key: ${key}`);
                Sentry.captureMessage(`${this.map.name} -- Icon resource not found for key: ${key}`, 'warning')
            }
            return res && res.url+'?image=1'
        },
        getGuideCameraConfig(prop, def){
            const queryProp = this.$route.query[`guide_camera.${prop}`];
            if( queryProp === 'false' ) return false;

            return queryProp || this.getMapConfigProp(`guide_camera.${prop}`, def)
        },
        getGuideConfig(prop, def){
            const queryProp = this.$route.query[`guide.${prop}`];
            if( queryProp === 'false' ) return false;

            return queryProp || this.getMapConfigProp(`guide.${prop}`, def)
        },
        resetGuide(){
            this.$emit('changeView', 'GuideView')
            this.guideRenderTrigger+=1
        },
        resetViews(){
            // Force views components to re-render
            this.guideRenderTrigger+=1;
            this.guideStepRenderTrigger+=1;
        },
        onFloorClick(e){
            const clickedFloor = this.visibleFloors.find( f=> f.unit.name === e);
            console.debug('FF', clickedFloor.unit.name, clickedFloor.floor.building_group_id);
        },

        resetFirstViewSettings(){
            if(this.isFirstView)
            {
                this.transitionDuration = this.getGuideCameraConfig('guide_transition_duration', 1.4);
                this.guideStepTransitionDuration = this.getGuideCameraConfig('guide_step_transition_duration', 1.4);
                this.maxZoomMargin = this.getGuideCameraConfig('guide_view_margin', 0.4);
                this.isFirstView = false;
            }
        },
        onTransitionStart(){
            this.isViewInTransition = true;
        },
        onTransitionEnd(){
            this.isViewInTransition = false;
            this.resetFirstViewSettings();
        },
    },
    watch:{
        guideSequenceFloorsUnitNames(value){
            console.debug('FF guide floors', value)
        },
        bgLevels2Expand(value){
            console.debug('FS: bgLevels2Expand', value);
        },
        bgEntranceLevels(value){
            console.debug('FS: bgEntranceLevels', value);
        },
        '$route'(route,oldRoute){
            if(oldRoute.name.includes('guide') && !route.name.includes('guide')){
                this.onRemovePath();
            }
        }
    },
    // -------------------------------- Hooks ---------------------------------------
    mounted() {
        this.initEventListeners()
        this.consoleDebug = console.debug;
        console.debug('FF guide vertexes floors', this.guideVerticesFloorsUnitNames);
    },
    beforeDestroy() {
        this.deactivateEventListeners()
    },
}
</script>
<style lang="scss">
  @import "o-map-guide-views";
</style>
